/* eslint guard-for-in: 0 */
import _ from 'lodash';
import { history } from '../history';

export const LAST_REFRESH_TOKEN_TIME = 'refreshTokenTime';
export const LOCK_REFRESH_TOKEN_KEY = 'refreshTokenLock'

/**
 * @function
 * @name cutSection
 * @description Subfunction for renameAndCut function
 * @param {string}
 * @returns {string}
 */
export const cutSection = (str) => {
  const firstCapital = str.match(/[A-Z]/g)[0];
  const firstCapitalIndex = str.split('').indexOf(firstCapital);
  const withoutSection = str.split('').splice(firstCapitalIndex);
  withoutSection[0] = withoutSection[0].toLowerCase();
  return withoutSection.join('');
};

/**
 * @function
 * @name renameProperty
 * @description Subfunction for renameAndCut function
 * @param {object} obj - object value
 * @param {number} key - key
 * @param {number} newKey - new key
 */
export const renameProperty = (obj, key, newKey) => {
  obj[newKey] = obj[key];
  delete obj[key];
};

/**
 * @function
 * @name renameAndCut
 * @description Func for cut section from properties (ticketUsername -> username)
 * @param {object} obj - object value
 * @returns {object}
 */
export const renameAndCut = (obj) => {
  const newObj = { ...obj };
  for (const key in newObj) {
    renameProperty(newObj, key, cutSection(key));
  }
  return newObj;
};

/**
 * @function
 * @name isValidURL
 * @description Validate url string
 * @param string - string value(url)
 * @returns {boolean} - valid/not valid
 */
export const isValidURL = (string) => {
  try {
    new window.URL(string);
    return true;
  } catch (_) {
    return false;
  }
};

/**
 * @function
 * @name escapeStringRegexp
 * @param {string} string
 * @returns {string}
 */
export const escapeStringRegexp = (string) => {
  if (typeof string !== 'string') {
    throw new TypeError('Expected a string');
  }

  return string.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&').replace(/-/g, '\\x2d');
};

/**
 * @function
 * @name sortByCount
 * @param a
 * @param b
 * @returns {number}
 */
export const sortByCount = (a, b) => {
  if (a.count < b.count) {
    return 1;
  }
  if (a.count > b.count) {
    return -1;
  }
  return 0;
};

/**
 * @function
 * @name delay
 * @param {number} ms
 * @returns {Promise>}
 */
export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @function
 * @name sortByRateAsc
 * @param a
 * @param b
 * @returns {number}
 */
export const sortByRateAsc = (a, b) => {
  const rateA = a.rate && a.rate.custom ? parseFloat(a.rate.custom) : false;
  const rateB = b.rate && b.rate.custom ? parseFloat(b.rate.custom) : false;

  if (rateA && rateB) {
    if (rateA < rateB) {
      return -1;
    }
    if (rateA > rateB) {
      return 1;
    }
  }
  return 0;
};

/**
 * @function
 * @name sortByRateDesc
 * @param a
 * @param b
 * @returns {number}
 */
export const sortByRateDesc = (a, b) => {
  const rateA = a.rate && a.rate.custom ? parseFloat(a.rate.custom) : false;
  const rateB = b.rate && b.rate.custom ? parseFloat(b.rate.custom) : false;

  if (rateA && rateB) {
    if (rateA < rateB) {
      return 1;
    }
    if (rateA > rateB) {
      return -1;
    }
  }
  return 0;
};

/**
 *
 * @param a
 * @param b
 * @returns {number}
 */
export const sortByPosition = (a, b) => {
  if (a.position < b.position) {
    return -1;
  }
  if (a.position > b.position) {
    return 1;
  }
  return 0;
};

/**
 * @function
 * @name isTouchDevice
 * @description Detect touch device
 * @returns {boolean} - true/false
 */
export const isTouchDevice = () => !!(
  typeof window !== 'undefined'
    && ('ontouchstart' in window || (window.DocumentTouch && typeof document !== 'undefined' && document instanceof window.DocumentTouch))
) || !!(typeof navigator !== 'undefined' && (navigator.maxTouchPoints || navigator.msMaxTouchPoints));

/**
 * @storageAvailable
 * @name logoutUser
 * @param type - Storage type
 * @description Detect logout
 * @returns {void}
 */
export const storageAvailable = (type) => {
  let storage;
  try {
    storage = window[type];
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException
      // everything except Firefox
      && (e.code === 22
        // Firefox
        || e.code === 1014
        // test name field too, because code might not be present
        // everything except Firefox
        || e.name === 'QuotaExceededError'
        // Firefox
        || e.name === 'NS_ERROR_DOM_QUOTA_REACHED')
      // acknowledge QuotaExceededError only if there's something already stored
      && storage
      && storage.length !== 0
    );
  }
};

/**
 * @function
 * @name redirectToMainPage
 * @description redirect to main page
 * @returns {void}
 */
export const redirectToMainPage = () => {
  localStorage.removeItem('access_token');
  history.push('/');
};
/**
 * @function
 * @name pageWrapperScrollTop
 * @description page wrapper scroll to top
 * @returns {void}
 */
export const pageWrapperScrollTop = () => {
  const wrapperNode = document.getElementById('page-wrapper');
  if (wrapperNode) {
    wrapperNode.scrollTop = 0;
  }
};
/**
 * @function
 * @name pageWrapperScrollTop
 * @description page wrapper scroll to top
 * @param columns {array} - список колонок для сортировки
 * @param name {string} - код колонки
 * @returns {any}
 */
export const getSortColumnByName = (columns, name) => {
  if (columns) {
    return columns.find((column) => column.name === name);
  }
  return null;
};
/**
 *
 * @name getHighlightingText
 * @description Получаем текст с подсветкой
 * @param {string} value - текст
 * @param {string|null} search - Строка поиск
 * @param {boolean} isHighlight - Подсвечивать или нет
 * @returns {{__html: string}}
 */
export const getHighlightingText = (value, search, isHighlight = true) => {
  const valueToString = value.toString();
  if (search && isHighlight) {
    return {
      __html: valueToString.replace(new RegExp(escapeStringRegexp(search.toLowerCase()), 'gi'), (match) => `<mark>${match}</mark>`),
    };
  }
  return {
    __html: `<span>${value}</span>`,
  };
};

/**
 *
 * @name getItemsBetweenIndex
 * @description Получить элементы массива между 2 индексами
 * @param {array} array - текст
 * @param {number} oldIndex - Строка поиск
 * @param {number} newIndex - Подсвечивать или нет
 * @param {function} filterFunction - Функция филтрации
 * @returns []
 */
export const getItemsBetweenIndex = (array, oldIndex, newIndex, filterFunction) => array.reduce((accumulator, currentValue, currentIndex) => {
  const firstIndex = oldIndex > newIndex ? newIndex : oldIndex;
  const lastIndex = oldIndex < newIndex ? newIndex : oldIndex;
  if (firstIndex <= currentIndex && lastIndex >= currentIndex) {
    if (filterFunction) {
      if (filterFunction(currentValue)) {
        accumulator.push(currentValue.id);
      }
      return accumulator;
    }
    accumulator.push(currentValue.id);
  }
  return accumulator;
}, []);

/**
 *
 * @name tryToParseToJson
 * @description Проверка на валидный json
 * @param {string} str - текст
 * @returns {Object | false}
 */
export const tryToParseToJson = (str) => {
  try {
    const result = JSON.parse(str);
    return result;
  } catch (e) {
    return false;
  }
};

/**
 *
 * @name getErrorMessage
 * @description Вывод ошибки
 * @param {object} response - catch error
 * @returns {string}
 */
export const getErrorMessage = (response) => {
  if (response) {
    if (_.isString(response)) {
      return response;
    }
    if (response.hasOwnProperty('message')) {
      return response.message;
    }
    if (response.hasOwnProperty('status')) {
      const { status } = response;
      switch (status) {
      case 404:
        if (response.data && response.data.error_message) {
          const { error_message } = response.data;
          return error_message;
        }
        return 'Route note found';
      default:
        if (response.data && response.data.error_message) {
          const { error_message } = response.data;
          return error_message;
        }
      }
    }
  }
  return 'Unknown error. Try again later.';
};

/**
 *
 * @name getRouteLinkById
 * @description Получит роут по ID
 * @returns {string}
 */
export const getRouteLinkById = () => ({
  name: 'Subscriptions',
  link: '/subscriptions',
  id: 7,
  react: true,
});

/**
 *
 * @name checkValidIP
 * @description Проверка на валидный ip(v4, v6, dual)
 * @param {string} str - текст
 * @returns {boolean}
 */
export const checkValidIP = (str) => {
  if (!str) return false;
  const regexExp = /((^\s*((([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5]))\s*$)|(^\s*((([0-9A-Fa-f]{1,4}:){7}([0-9A-Fa-f]{1,4}|:))|(([0-9A-Fa-f]{1,4}:){6}(:[0-9A-Fa-f]{1,4}|((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){5}(((:[0-9A-Fa-f]{1,4}){1,2})|:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3})|:))|(([0-9A-Fa-f]{1,4}:){4}(((:[0-9A-Fa-f]{1,4}){1,3})|((:[0-9A-Fa-f]{1,4})?:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){3}(((:[0-9A-Fa-f]{1,4}){1,4})|((:[0-9A-Fa-f]{1,4}){0,2}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){2}(((:[0-9A-Fa-f]{1,4}){1,5})|((:[0-9A-Fa-f]{1,4}){0,3}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(([0-9A-Fa-f]{1,4}:){1}(((:[0-9A-Fa-f]{1,4}){1,6})|((:[0-9A-Fa-f]{1,4}){0,4}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:))|(:(((:[0-9A-Fa-f]{1,4}){1,7})|((:[0-9A-Fa-f]{1,4}){0,5}:((25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}))|:)))(%.+)?\s*$))/;

  return regexExp.test(str);
};

/**
 *
 * @name formatNumber
 * @description Форматирование числа
 * @param {number} num - число исходное
 * @param {number} precision - точность после челой части
 * @returns {string}
 */
export const formatNumber = (num, precision = 2) => {
  const map = [
    { suffix: 'E', threshold: 1e18 },
    { suffix: 'T', threshold: 1e12 },
    { suffix: 'B', threshold: 1e9 },
    { suffix: 'M', threshold: 1e6 },
    { suffix: 'k', threshold: 1e3 },
    { suffix: '', threshold: 1 },
  ];
  const found = map.find((x) => Math.abs(num) >= x.threshold);
  if (found) {
    const formatted = (num / found.threshold).toFixed(precision) + found.suffix;
    return formatted;
  }
  return num;
};

export const toBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
});

export const getCurrentDomain = () => {
  const host = window?.location?.host || 'localhost:3000';
  const protocol = window?.location?.protocol || 'http:';

  return `${protocol}//${host}`;
};

export const copyToClipboard = (content) => {
  const el = document.createElement('textarea');
  el.value = content;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
};

export const uuid = () => ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16));

export const stripTags = (html, replaceValue) => html.replace(/<[^>]*>?/gm, replaceValue);
export const stripTagsWith = (html, tags = []) => {
  const tagStripper = new RegExp(`<[ /]*(${tags.join('|')})[^>]*>`, 'gi');
  return html.replace(tagStripper, '');
};

export const replaceHTMLEntities = (str) => {
  const htmlEntities = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#039;': "'",
    '&ndash;': '-',
  };

  return str.replace(/&[\w#]+;/g, (entity) => htmlEntities[entity] || entity);
};

export const parseJwt = (token) => {
  if (token) {
    try {
      const base64Url = token.split('.')[1];
      if (base64Url) {
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          window
            .atob(base64)
            .split('')
            .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
            .join(''),
        );
        return JSON.parse(jsonPayload);
      }
    } catch (error) {
      console.error(error);
    }
  }
  return {};
};

/**
 *
 * @param url
 * @returns {boolean}
 */
export const isUrlEditor = (url) => {
  if (url) {
    return url.indexOf('admin/editor') !== -1 || url.indexOf('/editor') !== -1;
  }
  return false;
};

/**
 *
 * @param oldList
 * @param newListRows
 * @returns {*[]}
 */
export const updateRows = (oldList, newListRows) => {
  if (newListRows?.hasOwnProperty('updated_rows')) {
    const { updated_rows, deleted_rows, prepended_rows, appended_rows } = newListRows;

    const body = oldList.filter((order) => !(deleted_rows.length && deleted_rows.includes(Number(order.id)))).map((order) => {
      if (updated_rows.length) {
        const findUpdateRow = updated_rows.find((item) => item.id === order.id);
        if (findUpdateRow) {
          return findUpdateRow
        }
        return order
      }
      return order
    })
    return [...prepended_rows, ...body, ...appended_rows];
  }
  return [...oldList]
}

/**
 * @name parseUrl
 * @param url {string}
 * @returns {{fragment: *, query: *, pathname: *}|null}
 */
export const parseUrl = (url) => {
  if (url) {
    const pattern = RegExp('^(([^:/?#]+):)?(//([^/?#]*))?([^?#]*)(\\?([^#]*))?(#(.*))?');
    const matches = url.match(pattern);
    return {
      pathname: matches[5],
      query: matches[7],
      fragment: matches[9],
    };
  }
  return null;
}

/**
 * @name formatDate
 * @param date
 * @returns {string}
 */
export const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  return `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
};

export const removeEmptyValues = (obj) => {
  const result = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];

      if (value === null) {
        continue;
      }

      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') {
          continue;
        }
      }

      result[key] = value;
    }
  }
  return result;
}

export const convertNumbersToStrings = (input) => {
  if (Array.isArray(input)) {
    return input.map((item) => convertNumbersToStrings(item));
  }
  if (typeof input === 'object' && input !== null) {
    return Object.fromEntries(
      Object.entries(input).map(([key, value]) => [key, convertNumbersToStrings(value)]),
    );
  }
  if (typeof input === 'number') {
    return String(input);
  }
  return input;
}

/*
 * @name subtractYears
 * @param dateStr
 * @param years
 * @returns {`${number}-${string}-${string}`}
 */
export const subtractYears = (dateStr, years = 10) => {
  if (typeof dateStr === 'number' && years === undefined) {
    years = dateStr;
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    dateStr = `${year}-${month}-${day}`;
  }

  if (!dateStr) {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    dateStr = `${year}-${month}-${day}`;
  }

  if (typeof years !== 'number') {
    throw new Error('Years must be a number');
  }

  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year - years, month - 1, day);

  const resultYear = date.getFullYear();
  const resultMonth = String(date.getMonth() + 1).padStart(2, '0');
  const resultDay = String(date.getDate()).padStart(2, '0');

  return `${resultYear}-${resultMonth}-${resultDay}`;
}
